// Central application configuration.
// All magic numbers, copy strings, and feature toggles live here.
// Future modules should import from this single location.

export const appConfig = {
  // ── Debug ────────────────────────────────────────────────────────────────
  // Set to true during development to enable structured debug logging.
  debug: true,

  // ── Timing constants ─────────────────────────────────────────────────────
  // Milliseconds before scanning guidance text appears in SCANNING state.
  scanningOverlayDelayMs: 500,

  // Milliseconds without anchor acquisition before escalating to SCAN_GUIDANCE.
  scanGuidanceTimeoutMs: 60000,

  // Milliseconds to allow small stabilization adjustments after whale reveal.
  stabilizationWindowMs: 4000,

  // Milliseconds for the AR view fade-in transition.
  arFadeInMs: 600,

  // ── Reveal threshold ─────────────────────────────────────────────────────
  // Horizontal aim threshold in degrees. Whale reveals when the camera forward
  // direction is within this many degrees of the intended whale direction.
  aimThresholdDeg: 45,

  // ── UI copy strings ──────────────────────────────────────────────────────
  copy: {
    introTagline: 'See a life-sized North Atlantic right whale in augmented reality.',
    beginCta: 'Begin Experience',

    scanning: 'Scanning for sign…',
    scanGuidance: 'Try moving closer to the sign and moving your phone slowly.',

    aimInstruction: 'Point your device toward the museum',
    trackingLost: 'Tracking lost—please rescan the sign',

    unsupported: 'This experience is not supported on your device.',
    permissionError: 'Camera access is required. Please enable it in your browser settings and reload the page.',
    loadError: 'Failed to load experience. Please reload the page.',

    infoButtonLabel: 'Info',
    infoModalTitle: 'North Atlantic Right Whale',
    infoModalBody:
      'The North Atlantic right whale (Eubalaena glacialis) is one of the world\'s most endangered large whales, with fewer than 360 individuals remaining. They are slow swimmers and spend time near the surface, historically making them a target for whalers—the "right" whale to hunt. Today, entanglement in fishing gear and vessel strikes are the primary threats to their survival. Conservation efforts are underway to protect their migratory corridors along the East Coast of North America.',
  },

  // ── Whale asset ──────────────────────────────────────────────────────────
  // GLB model with embedded material/texture, served from public/assets/.
  whaleAssetPath: '/assets/whale.glb',

  // ── Image target ─────────────────────────────────────────────────────────
  // Must match the name generated by: npx @8thwall/image-target-cli@latest
  // The corresponding target JSON must be placed in public/assets/targets/
  // imageTargetName: 'whale-sign',
  imageTargetName: 'women_whales_target',

  // Physical width of the printed sign in metres.
  // XR8 uses this value to calibrate the SLAM world scale via scale:'absolute'.
  // If this is wrong, all real-world translation is scaled by the ratio
  // (actual width / configured width) — e.g. a 0.5 m sign configured as 0.125 m
  // compresses translation 4×, so walking 4 m shows as 1 m in Three.js.
  // MUST be measured from the actual printed sign before on-site testing.
  imageTargetPhysicalWidthM: 0.125,

  // ── Whale placement defaults ──────────────────────────────────────────────
  // Developer-tunable transform applied relative to the image-target/sign
  // reference frame. Adjust these values during on-site calibration.
  whalePlacement: {
    // Offset from sign center in meters: [right, up, back]
    position: [0, 2, 10] as [number, number, number],
    // Euler rotation in degrees: [pitch, yaw, roll]
    rotation: [0, -90, 0] as [number, number, number],
    // Uniform scale — 1.0 represents true-to-life size relative to model units.
    scale: 1.0,
  },

  // ── Advanced rendering flags ──────────────────────────────────────────────
  // These are evaluated at runtime against device capability checks.
  // Setting a flag to false disables the feature regardless of device support.
  rendering: {
    enableLighting: true,
    enableShadows: true,
    enableOcclusion: true,
  },
} as const;
